import { useEtlJobCatalog } from './useEtlJobs'
import { deriveAdminGate } from '../utils/adminGate'
import type { AdminGate } from '../utils/adminGate'

/**
 * The server's answer to "does this caller hold Lighthouse.Admin", for the rail,
 * the role line, admin pages, previews and badges.
 *
 * Reads the job catalogue (shared query key `['etl-job-catalog']`, so the
 * Scheduler and this hook share one request), whose `permission` is the
 * LighthouseAdmin policy evaluated by the server — see `deriveAdminGate`.
 *
 * COST: the rail mounts this, so the catalogue is now fetched on every page, not
 * just on /jobs. It is cheap and cached — staleTime 60s, no polling — and it only
 * changes when Automaton restarts.
 *
 * CONTRACT for admin surfaces: on `denied`, render RestrictedState WITHOUT
 * fetching (an outer gate component mounts the fetching body only when not
 * denied, when the fetching hook has no `enabled` option); also render it when
 * the page's own query fails with errorKind 'forbidden'. On `pending` — the
 * catalogue has not answered yet, as on every cold load — fetch nothing and show
 * a skeleton: a request sent then races the answer that may refuse it. Pages
 * fetch on `admin` and `unknown` (utils/adminGate.ts adminPageMayFetch).
 * Previews and badges fetch ONLY on `admin` — a badge never polls an endpoint
 * the caller cannot read.
 */
export function useLighthouseAdmin(): AdminGate {
  const { data, isError } = useEtlJobCatalog()
  return deriveAdminGate(data, isError)
}
